import { useEffect, useState } from 'react'

import { AdminPeople } from './AdminPeople'
import { call, productTitle } from './adminApi'

import './admin.css'

interface AdminState {
  authorized: boolean
  paymentsEnabled: boolean
}

interface Summary {
  users: number
  profiles: number
  portraits: number
  dayCards: number
  oracleQuestions: number
  couples: number
  paidPurchases: number
  revenueRub: number
  llmCostRub: number
  paymentsEnabled: boolean
  products: { key: string; title: string; priceRub: number }[]
  cities: number
  chartsInMemory: number
}

interface Payment {
  productKey: string
  status: string
  amountRub: number
  manual: boolean
  createdAt: string
}

interface LlmStats {
  byPurpose: { purpose: string; model: string; calls: number; inputTokens: number; outputTokens: number; costRub: number }[]
  totalRub: number
}

type Tab = 'summary' | 'users' | 'payments' | 'llm' | 'settings'

const TABS: { id: Tab; title: string }[] = [
  { id: 'summary', title: 'Панель' },
  { id: 'users', title: 'Люди' },
  { id: 'payments', title: 'Платежи' },
  { id: 'llm', title: 'Модели' },
  { id: 'settings', title: 'Настройки' },
]

/**
 * Админка. Своего пароля нет: либо сквозной вход из панели, либо одноразовый
 * код, который бот присылает владельцу в Telegram.
 */
export function Admin() {
  const [state, setState] = useState<AdminState | null>(null)
  const [tab, setTab] = useState<Tab>('summary')
  const [summary, setSummary] = useState<Summary | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [llm, setLlm] = useState<LlmStats | null>(null)
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    call<AdminState>('/api/admin/state')
      .then(setState)
      .catch(() => setState({ authorized: false, paymentsEnabled: false }))
  }, [])

  useEffect(() => {
    if (!state?.authorized) return
    if (tab === 'summary' && !summary) call<Summary>('/api/admin/summary').then(setSummary).catch(() => undefined)
    if (tab === 'payments' && payments.length === 0) {
      call<{ payments: Payment[] }>('/api/admin/payments').then((data) => setPayments(data.payments)).catch(() => undefined)
    }
    if (tab === 'llm' && !llm) call<LlmStats>('/api/admin/llm').then(setLlm).catch(() => undefined)
  }, [state, tab, summary, payments.length, llm])

  if (!state) return <div className="state">…</div>

  if (!state.authorized) {
    return (
      <div className="screen no-tabs admin">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">админка</div>
        </div>
        <h1 className="display">Вход</h1>
        <div className="sub">Пароля здесь нет. Зайди кнопкой «админка» из панели — или пришли себе код в Telegram.</div>
        {!sent ? (
          <button
            type="button"
            className="btn btn-gold cta"
            disabled={busy}
            onClick={() => {
              setBusy(true)
              setError('')
              call('/api/admin/login/request', {})
                .then(() => setSent(true))
                .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
                .finally(() => setBusy(false))
            }}
          >
            Прислать код
          </button>
        ) : (
          <>
            <input
              className="field"
              inputMode="numeric"
              maxLength={4}
              placeholder="4 цифры"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
            <button
              type="button"
              className="btn btn-gold cta"
              disabled={busy || code.length !== 4}
              onClick={() => {
                setBusy(true)
                setError('')
                call('/api/admin/login/verify', { code })
                  .then(() => call<AdminState>('/api/admin/state'))
                  .then(setState)
                  .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
                  .finally(() => setBusy(false))
              }}
            >
              Войти
            </button>
          </>
        )}
        {error && <div className="field-warn">{error}</div>}
      </div>
    )
  }

  return (
    <div className="screen no-tabs admin">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">админка</div>
      </div>

      <div className="admin-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-tab${tab === item.id ? ' on' : ''}`}
            onClick={() => setTab(item.id)}
          >
            {item.title}
          </button>
        ))}
      </div>

      {tab === 'summary' && summary && (
        <div className="admin-grid">
          {[
            { title: 'Людей', value: summary.users },
            { title: 'С картой', value: summary.profiles },
            { title: 'Портретов', value: summary.portraits },
            { title: 'Карточек дня', value: summary.dayCards },
            { title: 'Вопросов', value: summary.oracleQuestions },
            { title: 'Пар', value: summary.couples },
            { title: 'Покупок', value: summary.paidPurchases },
            { title: 'Выручка, ₽', value: summary.revenueRub },
            { title: 'Модели, ₽', value: summary.llmCostRub },
            { title: 'Карт в памяти', value: summary.chartsInMemory },
          ].map((item) => (
            <div className="admin-cell" key={item.title}>
              <div className="admin-cell-title">{item.title}</div>
              <div className="admin-cell-value">{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && <AdminPeople />}

      {tab === 'payments' && (
        <div className="rowlist">
          {payments.map((payment, index) => (
            <div className="rowitem" key={`${payment.createdAt}-${index}`}>
              <div>
                <div className="ttl">{productTitle(payment.productKey)}</div>
                <div className="note">
                  {payment.manual ? 'выдано из админки' : `${payment.amountRub} ₽`} · {payment.status} ·{' '}
                  {new Date(payment.createdAt).toLocaleString('ru-RU')}
                </div>
              </div>
            </div>
          ))}
          {payments.length === 0 && <div className="state">платежей ещё не было</div>}
        </div>
      )}

      {tab === 'llm' && llm && (
        <>
          <div className="card admin-card">
            <div className="lbl">Расход за 30 дней</div>
            <div className="admin-value">{llm.totalRub} ₽</div>
          </div>
          <div className="rowlist">
            {llm.byPurpose.map((row) => (
              <div className="rowitem" key={`${row.purpose}-${row.model}`}>
                <div>
                  <div className="ttl">
                    {row.purpose} · {row.costRub} ₽
                  </div>
                  <div className="note">
                    {row.model} · {row.calls} вызовов · вход {row.inputTokens}, выход {row.outputTokens}
                  </div>
                </div>
              </div>
            ))}
            {llm.byPurpose.length === 0 && <div className="state">вызовов ещё не было</div>}
          </div>
        </>
      )}

      {tab === 'settings' && summary && (
        <>
          <div className="card admin-card">
            <div className="lbl">Оплата</div>
            <div className="admin-value">{summary.paymentsEnabled ? 'включена' : 'выключена флагом'}</div>
            <div className="note">
              Флаг PAYMENTS_ENABLED в окружении. Включать только после подтверждения цен.
            </div>
          </div>
          <div className="lbl sect">Цены</div>
          <div className="rowlist">
            {summary.products.map((product) => (
              <div className="rowitem" key={product.key}>
                <div>
                  <div className="ttl">{product.title}</div>
                  <div className="note">{product.key}</div>
                </div>
                <div className="right">{product.priceRub} ₽</div>
              </div>
            ))}
          </div>
          <div className="card admin-card">
            <div className="lbl">Справочник мест</div>
            <div className="admin-value">{summary.cities}</div>
          </div>
        </>
      )}

      <button
        type="button"
        className="btn btn-ghost cta"
        onClick={() => void call('/api/admin/logout', {}).then(() => setState({ ...state, authorized: false }))}
      >
        Выйти
      </button>
    </div>
  )
}
